import { initRUM, log } from "@hoylu/client-common";
import { createRoot } from "react-dom/client";
import { v4 as uuidV4 } from "uuid";
import "@hoylu/client-common/dist/assets/style/hoylu-global.css";
import "@hoylu/client-common/dist/assets/style/hoylu-ui-icons-centered.css";
import "@hoylu/client-common/dist/assets/style/fonts.css";
import "@hoylu/client-common/dist/assets/style/theme.css";
import "react-datepicker/dist/react-datepicker.css";
import packageJson from "../package.json";
import { English } from "./_localized/en";
import { connectFeatureFlagsToDispatch } from "./feature-flags-dispatch";
import { connectHistoryToDispatch } from "./history-state-dispatch";
import { connectPostMessageToDispatch } from "./post-message-dispatch";
import { createStore } from "./state";
import * as configActions from "./state/config/config.actions";
import * as userActions from "./state/user/user.actions";
import * as workspacesActions from "./state/workspaces/workspaces.actions";
import { Localized } from "./strings";
import renderApp from "./ui/App";
import { detectTeams } from "./utils/detectTeams";
import { detectWebex } from "./utils/detectWebex";
import {
  checkURLForProjectLink,
  checkURLPathForCommand,
  checkUrlPathForWorkspaceId,
  getProjectIdFromUrl,
  getSearchParamsForDeepLinks,
} from "./utils/url.helpers";

const logSessionId = uuidV4();

// store in session storage so that workspace in iframe can use the same one (Note: it has a different log object)
sessionStorage.logSessionId = logSessionId;
log.setSessionId(logSessionId);
log.setLabels({ appVersion: packageJson.version });

// initiating localization with default language, if there is needed additional language in locales it will be pushed after fetching user settings
Localized.pushLocale("en", English);

// preventing pinch zooming
document.addEventListener(
  "wheel",
  (e: WheelEvent) => {
    if (e.ctrlKey) {
      e.preventDefault();
    }
  },
  { passive: false },
);

const store = createStore();
const env = (window as any).__env;
store.dispatch(
  configActions.updateConfig({
    isDev: env === undefined,
    isBeta: window.origin.toLowerCase().includes("beta"),
    // Use default workspace ideas if env variable is undefined.
    // Notes: Empty string "" is indicating there is no workspace ideas workspace!
    //        WORKSPACE_IDEAS_ID is injected in app-dashboard.nomad, but not available (= undefined) when running locally, or in electron
    workspaceIdeasId: env?.WORKSPACE_IDEAS_ID ?? "608940606",
    zendeskWidgetOrigin:
      env?.ZENDESK_WIDGET_ORIGIN ??
      "https://app-zendesk-widget.westus2.tiger.stage.hoy.lu",
  }),
);

const devConfigUrl =
  localStorage.getItem("devConfigUrl") ??
  "https://hoylu.staging.eng.hoy.lu/api/v1/config";
const configUrl: string = env?.CONFIG_URL || devConfigUrl;

detectTeams(store);
detectWebex(store);

const workspaceToEdit = checkUrlPathForWorkspaceId(window.location.pathname);
if (workspaceToEdit) {
  store.dispatch(
    workspacesActions.queueEditWorkspace({ workspaceId: workspaceToEdit }),
  );
  const searchParams = getSearchParamsForDeepLinks();

  if (searchParams) {
    store.dispatch(
      workspacesActions.searchParamsForEditWorkspace({
        [workspaceToEdit]: searchParams.slice(1),
      }),
    );
  }
} else if (checkURLPathForCommand(window.location.pathname) === "new") {
  store.dispatch(workspacesActions.queueCreateWorkspace());
} else if (checkURLForProjectLink()) {
  const id = getProjectIdFromUrl();
  if (id) {
    store.dispatch(workspacesActions.queueProjectLoading(id));
  }
}

connectPostMessageToDispatch(store);
connectHistoryToDispatch(store);
connectFeatureFlagsToDispatch(store);

store.dispatch(configActions.getConfig.request({ configUrl }));
store.dispatch(userActions.getToken.request());

window.document.documentElement.onfullscreenchange = () => {
  store.dispatch(workspacesActions.handleFullscreenChange());
};

initRUM();

const start = () => {
  const container = document.getElementById("root");
  const root = createRoot(container!);
  renderApp(root, store);
};

start();
